import React, { useEffect } from 'react';
import { Accordion, Card, Button } from 'react-bootstrap';
import { set, get } from 'lodash';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { checkCurrency, SelectBoxStyles, shortenStringWithTooltip, roundingOff } from '../../../../../Components/helper';
import useDetailedCostingHooks from '../Hooks/useDetailedCostingHooks';
import DetailedCostingExpandView from './ExpandView/DetailedCostingExpandView';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import { detailedCostingFieldpath, errorpath } from '../../../ProductCosting/fieldPath';

export function DetailedCostingTable({ checkAccodionActiveState, handleClickToggle, productInfoObj }) {
  const { productCostingstate, setProductCostingState, globalError } = productInfoObj;
  const {
    detailedCosting: { bomTypeArr },
    productInfo: { currency },
    uomOptionsArr,
    isBreakdown,
    productId,
  } = productCostingstate;
  const {
    ProductCostingData: { productCostingDetail, productLengths },
  } = useSelector((state) => state);
  const { calculateDetailCosting } = useDetailedCostingHooks(productInfoObj);
  const { t } = useTranslation('common');

  const { bomDataError } = globalError;
  const { statusState, isDisabled, hideLengthCol, hideColourCol } = useShowHideSectionHooks(productInfoObj);
  const { onChangeCheckIfErrorExist } = useValidationHooks();

  const renderComponent = Array.isArray(bomTypeArr) && bomTypeArr.length > 0;

  const expandDetailedCostingChild = (index, chIndex) => {
    const copyState = productCostingstate;
    const path = `detailedCosting.bomTypeArr[${index}].detailedCostingInfo[${chIndex}].isExpanded`;
    const expanded = get(copyState, path, false);
    set(copyState, path, !expanded);
    setProductCostingState((state) => ({ ...state, ...copyState }));
  };

  useEffect(() => {
    const callFn = async () => {
      const copyState = productCostingstate;

      if (productCostingDetail.detailedCosting && Array.isArray(productCostingDetail.detailedCosting)) {
        const updatedArray = productCostingDetail?.detailedCosting.map((data) => {
          return {
            ...data.bomComponentId,
            quantityUnitOfMeasure: data?.quantityUnitOfMeasure,
            detailedCostingInfo: data.detailedCostingInfo,
          };
        });
        set(copyState, detailedCostingFieldpath().DETAILED_COSTING_DATA, updatedArray);
        await calculateDetailCosting(copyState);
      }
    };
    if (productCostingDetail && productId) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCostingDetail, productId, productLengths]);

  const handleInputChange = async (value, bomPath, detailedCostingPath) => {
    const copyState = productCostingstate;
    set(copyState, bomPath, value);
    let detailedCostingData = get(copyState, detailedCostingPath, []);
    detailedCostingData = detailedCostingData.map((keys) => {
      keys.expandData = keys.expandData.map((expKeys) => {
        expKeys.quantityUom = value;
        return expKeys;
      });
      return keys;
    });
    set(copyState, detailedCostingPath, detailedCostingData);
    setProductCostingState((state) => ({ ...state, ...copyState }));
    await calculateDetailCosting(copyState);
  };

  const updateError = (path) => onChangeCheckIfErrorExist(path, globalError);

  const truthyValue = (bomData, index) => {
    return isDisabled || statusState ? (
      bomData?.quantityUnitOfMeasure?.name || '--'
    ) : (
      <>
        <Select
          options={uomOptionsArr}
          onChange={(selected) => {
            handleInputChange(selected, detailedCostingFieldpath(index).BOM_QUANTITY_UOM, detailedCostingFieldpath(index).BOM_DETAILED_COSTING_DATA);
            if (get(bomDataError, `[${index}].quantityUnitOfMeasure`, '')) {
              updateError(errorpath(index).DETAILED.ERROR_UOM);
            }
          }}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          value={bomData.quantityUnitOfMeasure}
          isDisabled={statusState}
          styles={SelectBoxStyles}
          menuPosition="fixed"
          menuShouldBlockScroll
          menuShouldScrollIntoView
        />
        <span className="error-message">{get(bomDataError, `[${index}].quantityUnitOfMeasure`, '')}</span>
      </>
    );
  };

  return (
    <Accordion className="common-class-bottom average-colourway-table" activeKey={checkAccodionActiveState('bom')}>
      <Card className="mb-3 overflow-visible">
        <Card.Header>
          <span className="f-16 text-medium">{t('TAKING_DATES.BILL_OF_MATERIALS')}</span>
          <Accordion.Toggle
            className="btn-toggle"
            as={Button}
            variant="link"
            eventKey={checkAccodionActiveState('bom')}
            onClick={() => handleClickToggle('bom')}
          >
            <i className={checkAccodionActiveState('bom') ? 'icon-new-down-arrow-icon text-grey f-24 inverse' : 'icon-new-down-arrow-icon text-grey f-24'} />
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey={checkAccodionActiveState('bom')}>
          <Card.Body className="px-2 average-colourway-table">
            {renderComponent
              ? bomTypeArr?.map((bomData, index) => {
                  return (
                    <div key={bomData?._id} className="table-responsive ">
                      <table className="table common-custom-table layout-fixed ">
                        <thead>
                          <tr>
                            <th className="w-200px">{t('FILTER.BOM_TYPES')}</th>
                            <th className="w-200px">{t('TAKING_DATES.COMPONENT_CODE')}</th>
                            <th className="w-250px">{t('TAKING_DATES.COMPONET_DESCRIPTION')}</th>
                            <th className="w-200px">{t('TAKING_DATES.COMPONENT_TYPE')}</th>
                            <th className="w-200px">{t('FILTER.PLACEMENTS')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{bomData?.bomType?.name}</td>
                            <td>{bomData?.referenceCode}</td>
                            <td>
                              <div className="text-truncate">
                                <span title={bomData?.bomDescription}>{shortenStringWithTooltip(bomData?.bomDescription, 40)}</span>
                              </div>
                            </td>
                            <td>{bomData?.bomSubType?.name}</td>
                            <td>{bomData?.placement}</td>
                          </tr>
                        </tbody>
                      </table>
                      <table className="table common-custom-table layout-fixed" style={{ marginTop: '-1px' }}>
                        <thead>
                          <tr>
                            <th className="w-50px" />
                            {!hideColourCol && <th className="w-150px">{t('COSTING_TO_DO_LIST.COLORWAY')}</th>}
                            {!hideColourCol && <th className="w-150px">{t('TAKING_DATES.COLOR_REF')}</th>}
                            <th className="w-150px">{isBreakdown ? 'Size Range' : 'Size'}</th>
                            {hideLengthCol && <th className="w-150px">{isBreakdown ? 'Length Range' : 'Length'}</th>}
                            <th className="w-120px">{t('FILTER.CONSUMPTION')}</th>
                            <th className="w-120px">{t('TAKING_DATES.UOM')}</th>
                            <th className="w-120px">{t('COSTING_TO_DO_LIST.PRICE_PER_UNIT')}</th>
                            <th className="w-100px">{t('FILTER.TOTAL_PRICE')}</th>
                            <th className="w-100px">{t('TAKING_DATES.EXCLUDE')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {get(bomData, 'detailedCostingInfo', [])?.map((data, chIndex) => {
                            return !data.isExpanded && data?.expandData.length !== 1 && !isBreakdown ? (
                              <tr key={chIndex}>
                                <td>
                                  {data?.expandData &&
                                    data?.expandData.length > 0 &&
                                    (data.isExpanded ? (
                                      <button
                                        className="btn-up-down"
                                        type="button"
                                        aria-label={t('COMMON_LABEL.OPEN')}
                                        onClick={() => {
                                          expandDetailedCostingChild(index, chIndex);
                                        }}
                                      >
                                        <span className="icon-new-down-arrow-icon icon-inverse" />
                                      </button>
                                    ) : (
                                      <button
                                        className="btn-up-down"
                                        type="button"
                                        aria-label={t('COMMON_LABEL.CLOSE')}
                                        onClick={() => {
                                          expandDetailedCostingChild(index, chIndex);
                                        }}
                                      >
                                        <span className="icon-new-down-arrow-icon" />
                                      </button>
                                    ))}
                                </td>
                                {!hideColourCol && <td>{data?.colourway?.name}</td>}
                                {!hideColourCol && <td>{data?.colourway?.newReferences}</td>}
                                <td>
                                  {!hideColourCol &&
                                    (data.isExpanded ? '--' : Array.isArray(data?.totSize) && shortenStringWithTooltip(data?.totSize.join(', '), 15))}
                                  {hideColourCol && data?.sizeRange[0]?.name}
                                </td>

                                {hideLengthCol && (
                                  <td>{data.isExpanded ? '--' : Array.isArray(data?.totLength) && shortenStringWithTooltip(data?.totLength.join(', '), 25)}</td>
                                )}

                                <td className="text-left">--</td>

                                <td>{chIndex === 0 ? truthyValue(bomData, index) : bomData?.quantityUnitOfMeasure?.name}</td>
                                <td className="text-left">--</td>

                                <td className="text-left">--</td>
                                <td className="text-left">--</td>
                              </tr>
                            ) : (
                              (data.isExpanded || data.expandData.length === 1 || isBreakdown) && (
                                <DetailedCostingExpandView
                                  expandData={isBreakdown ? [data?.expandData[0]] : data?.expandData}
                                  index={index}
                                  chIndex={chIndex}
                                  isExpanded={data?.isExpanded}
                                  expandDetailedCostingChild={expandDetailedCostingChild}
                                  colourway={data?.colourway}
                                  quantityUnitOfMeasure={bomData?.quantityUnitOfMeasure}
                                  sizeRange={data?.sizeRange}
                                  lengthRange={data?.lengthRange}
                                  productInfoObj={productInfoObj}
                                />
                              )
                            );
                          })}

                          <tr>
                            <td />
                            <td colSpan="2">
                              <span className="f-16 text-bold">
                                {!hideColourCol &&
                                  (bomData?.totColourways?.length > 1
                                    ? `${bomData?.totColourways.length} Colourway(s)`
                                    : `${bomData?.totColourways.length || 0} Colourway`)}
                              </span>
                            </td>
                            <td>
                              <span className="f-16 text-bold">
                                {bomData?.totSize?.length > 1 ? `${bomData?.totSize.length} Size(s)` : `${bomData?.totSize.length || 0} Size`}
                              </span>
                            </td>
                            {hideLengthCol && (
                              <td>
                                <span className="f-16 text-bold">
                                  {bomData?.totLength?.length > 1 ? `${bomData?.totLength.length} Length(s)` : `${bomData?.totLength.length || 0} Length`}
                                </span>
                              </td>
                            )}
                            <td />
                            <td>
                              <span className="f-16 text-bold">{bomData?.quantityUnitOfMeasure?.name}</span>
                            </td>
                            <td colSpan="1" className="text-left">
                              --
                            </td>
                            <td colSpan="2" className="text-left">
                              <span className="f-16 text-bold">
                                {checkCurrency(currency, bomData?.overAllSumOfTotalPrice)} {roundingOff(bomData?.overAllSumOfTotalPrice, 4, true)}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })
              : null}
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
}

export default DetailedCostingTable;
